class CompareDrawer extends HTMLElement {
  connectedCallback() {
    this.slotsEl = this.querySelector('#CompareDrawer-Slots');
    this.tabsEl = this.querySelector('#CompareDrawer-Tabs');
    this.toggleEl = this.querySelector('.compare-drawer__toggle');
    this.innerEl = this.querySelector('.compare-drawer__inner');
    this.isDesignMode = this.dataset.designMode === 'true';
    this.justAutoExpandedAt = 0;
    this.previousIds = BtCompareUtil.getAllIds();
    this.activeCategory = null;
    this.slotsToken = 0;

    const storedCollapsed = BtStorageUtil.get(CompareDrawer.collapseStorageKey, true) === true;
    this.setCollapsed(storedCollapsed);

    this.toggleEl.addEventListener('click', () => this.setCollapsed(!this.collapsed));

    // clicking the collapsed bar (outside toggle/action buttons) expands it
    this.innerEl.addEventListener('click', (event) => {
      if (!this.collapsed) return;
      if (event.target.closest('.compare-drawer__toggle')) return;
      if (event.target.closest('.compare-drawer__actions-buttons')) return;
      this.setCollapsed(false);
    });

    this.querySelector('.compare-drawer__clear').addEventListener('click', () => {
      BtCompareUtil.clearAll();
    });

    this.onCompareUpdatedHandler = () => this.sync();
    document.addEventListener('compareUpdated', this.onCompareUpdatedHandler);

    // keeps the active tab in sync with the modal
    this.onActiveCategoryChangedHandler = () => this.sync();
    document.addEventListener('compareActiveCategoryChanged', this.onActiveCategoryChangedHandler);

    this.onStorageHandler = (event) => {
      if (event.key === 'bt-compare') this.sync();
      if (event.key === `bt-${BtCompareUtil.activeCategoryKey}`) this.sync();
      if (event.key === `bt-${CompareDrawer.collapseStorageKey}`) {
        this.setCollapsed(event.newValue === 'true');
      }
    };
    window.addEventListener('storage', this.onStorageHandler);

    this.onDocumentClickHandler = (event) => this.handleOutsideClick(event);
    document.addEventListener('click', this.onDocumentClickHandler);

    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('compareUpdated', this.onCompareUpdatedHandler);
    document.removeEventListener('compareActiveCategoryChanged', this.onActiveCategoryChangedHandler);
    window.removeEventListener('storage', this.onStorageHandler);
    document.removeEventListener('click', this.onDocumentClickHandler);
  }

  handleOutsideClick(event) {
    if (this.collapsed || this.hasAttribute('hidden')) return;
    // composedPath() instead of contains(): a clicked tab can remove itself from the DOM mid-bubble
    if (event.composedPath().includes(this)) return;
    // adding a product elsewhere on the page isn't a dismiss gesture
    if (event.target.closest('compare-add-button')) return;
    // modal is covering the drawer, so clicks in it aren't really "outside"
    const modal = document.getElementById('Compare-Modal');
    if (modal && modal.hasAttribute('open')) return;
    // ignore the click that just auto-expanded the drawer
    if (Date.now() - this.justAutoExpandedAt < 100) return;
    this.setCollapsed(true);
  }

  setCollapsed(collapsed) {
    this.collapsed = collapsed;
    BtStorageUtil.set(CompareDrawer.collapseStorageKey, collapsed);
    if (collapsed) {
      this.setAttribute('data-collapsed', 'true');
    } else {
      this.removeAttribute('data-collapsed');
    }
    this.toggleEl.setAttribute('aria-expanded', String(!collapsed));
  }

  showDrawer() {
    clearTimeout(this.hideTimeout);
    if (this.classList.contains('compare-drawer--visible')) return;

    this.removeAttribute('hidden');
    // double rAF so the off-screen position paints before the slide-up starts
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('compare-drawer--visible');
      });
    });
  }

  hideDrawer() {
    clearTimeout(this.hideTimeout);
    if (this.hasAttribute('hidden')) return;

    this.classList.remove('compare-drawer--visible');
    this.hideTimeout = setTimeout(() => {
      this.setAttribute('hidden', '');
    }, 400);
  }

  sync() {
    const allIds = BtCompareUtil.getAllIds();
    const categories = BtCompareUtil.getAllCategories();
    const shouldShow = categories.length > 0 || this.isDesignMode;

    // a product was added, not just removed — expand the drawer
    if (allIds.length > this.previousIds.length) {
      this.setCollapsed(false);
      this.justAutoExpandedAt = Date.now();
    }
    this.previousIds = allIds;

    document.body.classList.toggle('compare-drawer-open', shouldShow);

    if (!shouldShow) {
      this.activeCategory = null;
      this.setCollapsed(false);
      this.hideDrawer();
      this.tabsEl.innerHTML = '';
      this.slotsEl.innerHTML = '';
      return;
    }

    // fall back to the first populated category if the active one emptied out
    let activeCategory = BtCompareUtil.getActiveCategory();
    if (!categories.includes(activeCategory)) {
      activeCategory = categories[0];
      BtCompareUtil.setActiveCategory(activeCategory);
    }
    this.activeCategory = activeCategory;

    this.showDrawer();
    this.renderTabs(categories);
    this.renderSlots(BtCompareUtil.getCompareArray(activeCategory));
  }

  renderTabs(categories) {
    this.tabsEl.innerHTML = '';

    if (categories.length < 2) return;

    categories.forEach((category) => {
      const count = BtCompareUtil.getCompareArray(category).length;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'compare-drawer__tab';
      tab.setAttribute('aria-selected', String(category === this.activeCategory));
      if (category === this.activeCategory) tab.classList.add('compare-drawer__tab--active');
      tab.innerHTML = `<span>${category}</span><span class="compare-drawer__tab-count">${count}</span>`;
      tab.addEventListener('click', () => this.switchTab(category));
      this.tabsEl.appendChild(tab);
    });
  }

  switchTab(category) {
    if (category === this.activeCategory) return;
    this.activeCategory = category;
    BtCompareUtil.setActiveCategory(category);
    this.renderTabs(BtCompareUtil.getAllCategories());
    this.renderSlots(BtCompareUtil.getCompareArray(category));
  }

  renderSlots(ids) {
    // Clicking several "compare" buttons in quick succession fires one sync() per click, each
    // starting its own fetch here — without this token an earlier (now-stale) response could
    // resolve last and win, overwriting the bar with an outdated product list
    const requestToken = ++this.slotsToken;

    if (ids.length === 0) {
      this.renderSlotNodes([]);
      return;
    }

    const idsQuery = ids.map((id) => `id:${id}`).join(' OR ');
    fetch(`${window.routes.search_url}?section_id=compare-drawer&q=${idsQuery}&type="product"`)
      .then((response) => response.text())
      .then((response) => {
        if (requestToken !== this.slotsToken) return;

        const html = new DOMParser().parseFromString(response, 'text/html');
        const results = html.querySelector('.compare-drawer-results');
        if (!results) return;

        const filledSlots = Array.from(results.querySelectorAll('.compare-drawer__slot--filled'))
          .sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId));
        this.renderSlotNodes(filledSlots);
      });
  }

  renderSlotNodes(filledSlots) {
    this.slotsEl.innerHTML = '';

    filledSlots.forEach((slot) => {
      const removeButton = slot.querySelector('.compare-drawer__slot-remove');
      if (removeButton) {
        removeButton.addEventListener('click', () => {
          BtCompareUtil.removeCompareProduct(removeButton.dataset.productId);
        });
      }
      this.slotsEl.appendChild(slot);
    });

    const emptyCount = BtCompareUtil.maxItems - filledSlots.length;
    for (let i = 0; i < emptyCount; i++) {
      this.slotsEl.appendChild(this.buildEmptySlot());
    }
  }

  buildEmptySlot() {
    const slot = document.createElement('div');
    slot.className = 'compare-drawer__slot compare-drawer__slot--empty';
    const label = window.addedCompareStrings?.selectAnotherProduct || 'Select another product';
    slot.innerHTML = `
      <modal-opener data-modal="#Compare-Modal" class="compare-drawer__slot-empty-opener">
        <button type="button" class="compare-drawer__slot-empty-button unstyled-button">
          <svg width="56" height="56" viewBox="0 0 180 180" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M46.3926 124.652V75.8171H127.553V84.9461C129.033 85.9839 130.436 87.137 131.743 88.4247C132.3 88.982 132.8 89.5778 133.319 90.1543V71.6851L121.557 48.2959H52.3888L40.627 71.6851V130.398H86.6366C85.0799 128.611 83.7346 126.689 82.6391 124.633H46.3926V124.652ZM55.9443 54.0807H117.982L126.016 70.0515H47.8916L55.9251 54.0807H55.9443Z" fill="#0A5A5A"/>
            <path d="M133.665 129.918L133.3 129.553L130.59 126.843L129.552 125.805C131.147 123.806 132.396 121.615 133.3 119.271C134.395 116.426 134.991 113.37 134.991 110.238C134.991 107.105 134.395 104.049 133.3 101.205C132.051 97.9761 130.167 95.0164 127.649 92.4988C127.611 92.4604 127.573 92.4411 127.534 92.4027C122.806 87.7325 116.56 85.1572 109.911 85.1572C103.261 85.1572 96.9187 87.771 92.1716 92.4988C87.4438 97.2266 84.8301 103.53 84.8301 110.238C84.8301 115.484 86.4445 120.462 89.4041 124.652C90.2305 125.824 91.1338 126.939 92.1716 127.977C93.0749 128.88 94.0551 129.687 95.0545 130.417C99.3402 133.588 104.472 135.318 109.891 135.318C115.311 135.318 120.462 133.588 124.728 130.417C124.978 130.244 125.209 130.071 125.459 129.879L125.997 130.417L126.496 130.917L129.571 133.992L136.625 141.045L140.699 136.971L133.646 129.918H133.665ZM96.2652 123.902C92.6137 120.251 90.6149 115.408 90.6149 110.257C90.6149 105.106 92.6137 100.244 96.2652 96.6116C99.9168 92.96 104.76 90.9613 109.911 90.9613C115.061 90.9613 119.924 92.96 123.556 96.6116C125.247 98.3029 126.573 100.263 127.515 102.377C128.61 104.837 129.206 107.489 129.206 110.257C129.206 113.024 128.61 115.696 127.515 118.137C126.573 120.251 125.247 122.211 123.556 123.902C123.287 124.171 122.999 124.421 122.71 124.671C119.174 127.823 114.677 129.572 109.891 129.572C105.106 129.572 100.609 127.823 97.0724 124.671C96.7841 124.421 96.4959 124.171 96.2268 123.902H96.2652Z" fill="#0A5A5A"/>
            <path d="M113.204 107.912H122V112.932H113.204V122H107.75V112.932H99V107.912H107.75V99H113.204V107.912Z" fill="#0A5A5A"/>
          </svg>
          <span class="compare-drawer__slot-empty-label">${label}</span>
        </button>
      </modal-opener>
    `;
    return slot;
  }
}

CompareDrawer.collapseStorageKey = 'compare-drawer-collapsed';

customElements.define('compare-drawer', CompareDrawer);
